import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon,
  SparklesIcon,
  ChartBarIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
// @ts-ignore
import jsPDF from 'jspdf';
import logoWhite from '../assets/logos/logo-white.png';
import { buildAssessmentInsights } from '../utils/assessmentRules';
import Toast from './Toast';

interface Question {
  id: string;
  category: string;
  question: string;
  description?: string;
  type: 'single' | 'multiple' | 'scale' | 'matrix';
  options: {
    value: string | number;
    label: string;
    points: number;
    aiReadiness?: string;
  }[];
  weight: number;
}

interface AssessmentResults {
  totalScore: number;
  maxScore: number;
  grade: string;
  categoryScores: {
    [key: string]: {
      score: number;
      maxScore: number;
      percentage: number;
      recommendations: string[];
    };
  };
  quickWins: string[];
  recommendations: string[];
  estimatedROI: string;
  readinessLevel: string;
}

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(11, 152, 206, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(11, 152, 206, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(11, 152, 206, 0);
  }
`;

const AssessmentContainer = styled.div`
  padding: var(--spacing-2xl);
  background: transparent;
  position: relative;
  
  @media (max-width: 768px) {
    padding: var(--spacing-xl);
  }
`;

const ProgressSection = styled.div`
  margin-bottom: var(--spacing-2xl);
`;

const ProgressBar = styled.div`
  height: 6px;
  background: rgba(11, 152, 206, 0.1);
  border-radius: 3px;
  overflow: hidden;
  position: relative;
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, var(--color-accent-primary) 0%, var(--color-accent-secondary) 100%);
  width: ${props => props.progress}%;
  transition: width 0.5s cubic-bezier(0.65, 0, 0.35, 1);
  border-radius: 3px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    animation: shimmer 2s infinite;
  }
  
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const ProgressText = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: var(--spacing-sm);
  font-size: var(--fs-sm);
  color: var(--text-secondary);
`;

const QuestionContainer = styled.div`
  animation: ${slideIn} 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  min-height: 400px;
`;

const QuestionHeader = styled.div`
  margin-bottom: var(--spacing-2xl);
`;

const CategoryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-lg);
  background: linear-gradient(135deg, rgba(11, 152, 206, 0.1), rgba(11, 152, 206, 0.05));
  color: var(--color-accent-primary);
  border: 1px solid rgba(11, 152, 206, 0.2);
  border-radius: 50px;
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--spacing-lg);
  animation: ${fadeInUp} 0.5s ease;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const QuestionText = styled.h3`
  font-size: var(--fs-2xl);
  color: var(--text-primary);
  margin-bottom: var(--spacing-sm);
  line-height: 1.4;
  font-weight: var(--font-weight-semibold);
  animation: ${fadeInUp} 0.5s ease 0.1s both;
`;

const QuestionDescription = styled.p`
  color: var(--text-secondary);
  font-size: var(--fs-md);
  line-height: 1.6;
  animation: ${fadeInUp} 0.5s ease 0.2s both;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const OptionCard = styled.button<{ selected: boolean }>`
  padding: var(--spacing-lg) var(--spacing-xl);
  text-align: left;
  border: 2px solid ${props => props.selected 
    ? 'var(--color-accent-primary)' 
    : 'rgba(11, 152, 206, 0.15)'};
  background: ${props => props.selected 
    ? 'linear-gradient(135deg, rgba(11, 152, 206, 0.08), rgba(11, 152, 206, 0.03))' 
    : 'white'};
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.5s ease ${props => props.tabIndex ? props.tabIndex * 0.05 : 0}s both;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, transparent, rgba(11, 152, 206, 0.1));
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(11, 152, 206, 0.15);
    border-color: var(--color-accent-primary);
    
    &::before {
      opacity: 1;
    }
  }

  ${props => props.selected && css`
    animation: ${pulse} 1s cubic-bezier(0.16, 1, 0.3, 1);
    box-shadow: 0 4px 16px rgba(11, 152, 206, 0.2);
    
    &::after {
      content: '';
      position: absolute;
      right: var(--spacing-lg);
      top: 50%;
      transform: translateY(-50%);
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: var(--color-accent-primary);
      display: flex;
      align-items: center;
      justify-content: center;
    }
  `}
  
  span {
    position: relative;
    z-index: 1;
    font-size: var(--fs-md);
    color: var(--text-primary);
    line-height: 1.5;
  }
`;

const CheckMark = styled(CheckCircleIcon)`
  position: absolute;
  right: var(--spacing-lg);
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  color: var(--color-accent-primary);
`;

const ScaleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-xs);
  margin-top: var(--spacing-xl);
  padding: var(--spacing-sm);
  background: rgba(11, 152, 206, 0.03);
  border-radius: 12px;
`;

const ScaleButton = styled.button<{ selected: boolean }>`
  flex: 1;
  padding: var(--spacing-md);
  border: 2px solid ${props => props.selected 
    ? 'var(--color-accent-primary)' 
    : 'transparent'};
  background: ${props => props.selected 
    ? 'var(--color-accent-primary)' 
    : 'white'};
  color: ${props => props.selected 
    ? 'white' 
    : 'var(--text-secondary)'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: var(--font-weight-semibold);
  font-size: var(--fs-lg);
  box-shadow: ${props => props.selected 
    ? '0 4px 12px rgba(11, 152, 206, 0.3)' 
    : '0 2px 4px rgba(0, 0, 0, 0.05)'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(11, 152, 206, 0.2);
    border-color: var(--color-accent-primary);
    ${props => !props.selected && css`
      background: rgba(11, 152, 206, 0.05);
    `}
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ScaleLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: var(--spacing-sm);
  padding: 0 var(--spacing-sm);
`;

const ScaleLabel = styled.span<{ align?: string }>`
  font-size: var(--fs-xs);
  color: var(--text-secondary);
  text-align: ${props => props.align || 'center'};
  flex: ${props => props.align ? 'none' : '1'};
`;

const NavigationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--spacing-3xl);
  padding-top: var(--spacing-xl);
  border-top: 1px solid rgba(11, 152, 206, 0.1);
`;

const NavButton = styled.button<{ primary?: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-xl);
  background: ${props => props.primary 
    ? 'linear-gradient(135deg, var(--color-accent-primary), var(--color-accent-secondary))' 
    : 'white'};
  color: ${props => props.primary 
    ? 'white' 
    : 'var(--text-primary)'};
  border: ${props => props.primary 
    ? 'none' 
    : '2px solid rgba(11, 152, 206, 0.2)'};
  border-radius: 12px;
  font-weight: var(--font-weight-semibold);
  font-size: var(--fs-md);
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  opacity: ${props => props.disabled ? 0.5 : 1};
  box-shadow: ${props => props.primary 
    ? '0 4px 16px rgba(11, 152, 206, 0.3)' 
    : '0 2px 8px rgba(0, 0, 0, 0.05)'};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${props => props.primary 
      ? '0 8px 24px rgba(11, 152, 206, 0.4)' 
      : '0 4px 12px rgba(11, 152, 206, 0.15)'};
    ${props => !props.primary && css`
      background: rgba(11, 152, 206, 0.05);
      border-color: var(--color-accent-primary);
    `}
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const StepIndicator = styled.span`
  padding: var(--spacing-xs) var(--spacing-md);
  background: rgba(11, 152, 206, 0.1);
  color: var(--color-accent-primary);
  border-radius: 20px;
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-medium);
`;

// Results Components
const ResultsContainer = styled.div`
  animation: ${fadeInUp} 0.5s ease;
`;

const GradeSection = styled.div`
  text-align: center;
  padding: var(--spacing-2xl);
  background: linear-gradient(135deg, rgba(11, 152, 206, 0.05), rgba(11, 152, 206, 0.02));
  border-radius: 24px;
  margin-bottom: var(--spacing-2xl);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(11, 152, 206, 0.1) 0%, transparent 70%);
    animation: rotate 20s linear infinite;
  }
  
  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const GradeLetter = styled.div<{ grade: string }>`
  font-size: 6rem;
  font-weight: var(--font-weight-bold);
  background: ${props => {
    switch(props.grade) {
      case 'A': return 'linear-gradient(135deg, #10b981, #059669)';
      case 'B': return 'linear-gradient(135deg, #3b82f6, #2563eb)';
      case 'C': return 'linear-gradient(135deg, #f59e0b, #d97706)';
      case 'D': return 'linear-gradient(135deg, #ef4444, #dc2626)';
      default: return 'linear-gradient(135deg, #6b7280, #4b5563)';
    }
  }};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: var(--spacing-md);
  position: relative;
  z-index: 1;
  line-height: 1;
  animation: ${pulse} 2s ease-in-out;
`;

const ReadinessLevel = styled.h3`
  font-size: var(--fs-xl);
  color: var(--text-primary);
  margin-bottom: var(--spacing-sm);
  position: relative;
  z-index: 1;
`;

const ScoreDisplay = styled.p`
  font-size: var(--fs-lg);
  color: var(--text-secondary);
  position: relative;
  z-index: 1;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-2xl);
`;

const MetricCard = styled.div`
  padding: var(--spacing-lg);
  background: white;
  border-radius: 16px;
  border: 1px solid rgba(11, 152, 206, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(11, 152, 206, 0.15);
    border-color: var(--color-accent-primary);
  }
  
  h4 {
    font-size: var(--fs-sm);
    color: var(--text-secondary);
    margin-bottom: var(--spacing-xs);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  p {
    font-size: var(--fs-2xl);
    font-weight: var(--font-weight-bold);
    color: var(--color-accent-primary);
    margin: 0;
  }
`;

const QuickWinsCard = styled.div`
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.05));
  border: 2px solid #10b981;
  border-radius: 16px;
  padding: var(--spacing-xl);
  margin-bottom: var(--spacing-xl);
  
  h3 {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    color: #059669;
    margin-bottom: var(--spacing-lg);
    font-size: var(--fs-xl);
    
    svg {
      width: 24px;
      height: 24px;
    }
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    
    li {
      padding: var(--spacing-sm) 0;
      padding-left: var(--spacing-xl);
      position: relative;
      color: var(--text-primary);
      line-height: 1.6;
      
      &::before {
        content: '→';
        position: absolute;
        left: 0;
        color: #10b981;
        font-weight: bold;
        font-size: var(--fs-lg);
      }
    }
  }
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const FormInput = styled.input`
  padding: var(--spacing-md) var(--spacing-lg);
  font-size: var(--fs-md);
  border: 2px solid rgba(11, 152, 206, 0.2);
  border-radius: 12px;
  background: white;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-primary);
    box-shadow: 0 0 0 3px rgba(11, 152, 206, 0.1);
  }
  
  &::placeholder {
    color: var(--text-tertiary);
  }
`;

const questions: Question[] = [
  {
    id: 'current-tech',
    category: 'Technology Infrastructure',
    question: 'How would you describe your current technology stack?',
    description: 'Understanding your technical foundation helps us recommend the right AI implementation approach.',
    type: 'single',
    weight: 1.5,
    options: [
      { value: 'modern', label: 'Modern cloud-based systems (less than 3 years old)', points: 10 },
      { value: 'mixed', label: 'Mix of modern and legacy systems', points: 7 },
      { value: 'legacy', label: 'Mostly legacy systems (5+ years old)', points: 4 },
      { value: 'minimal', label: 'Minimal technology infrastructure', points: 2 }
    ]
  },
  {
    id: 'data-maturity',
    category: 'Data Readiness',
    question: 'How organized is your business data?',
    description: 'AI thrives on clean, accessible data. Better data organization means faster AI implementation.',
    type: 'single',
    weight: 2,
    options: [
      { value: 'centralized', label: 'Centralized data warehouse with clean, structured data', points: 10 },
      { value: 'partially', label: 'Some systems integrated, mostly structured', points: 7 },
      { value: 'siloed', label: 'Data in separate systems, limited integration', points: 4 },
      { value: 'scattered', label: 'Scattered across spreadsheets and documents', points: 2 }
    ]
  },
  {
    id: 'process-documentation',
    category: 'Process Maturity',
    question: 'Are your business processes documented?',
    description: 'Well-documented processes are easier to enhance with AI automation.',
    type: 'single',
    weight: 1.2,
    options: [
      { value: 'comprehensive', label: 'Comprehensive documentation, regularly updated', points: 10 },
      { value: 'good', label: 'Most key processes documented', points: 7 },
      { value: 'some', label: 'Some documentation, but outdated', points: 4 },
      { value: 'minimal', label: 'Little to no formal documentation', points: 2 }
    ]
  },
  {
    id: 'team-readiness',
    category: 'Team Adaptability',
    question: 'How does your team feel about new technology?',
    type: 'scale',
    weight: 1.3,
    options: [
      { value: 1, label: 'Very Resistant', points: 2 },
      { value: 2, label: 'Hesitant', points: 4 },
      { value: 3, label: 'Neutral', points: 6 },
      { value: 4, label: 'Open', points: 8 },
      { value: 5, label: 'Eager', points: 10 }
    ]
  },
  {
    id: 'current-automation',
    category: 'Automation Level',
    question: 'What automation currently exists?',
    type: 'single',
    weight: 1.4,
    options: [
      { value: 'advanced', label: 'Significant automation with integrated systems', points: 10 },
      { value: 'moderate', label: 'Some automated workflows and tools', points: 7 },
      { value: 'basic', label: 'Basic automation (email, simple workflows)', points: 4 },
      { value: 'manual', label: 'Mostly manual processes', points: 2 }
    ]
  },
  {
    id: 'budget-allocation',
    category: 'Financial Readiness',
    question: 'What percentage of revenue goes to innovation?',
    description: 'This helps us recommend solutions that fit your investment capacity.',
    type: 'single',
    weight: 1.1,
    options: [
      { value: 'high', label: 'More than 10% of revenue', points: 10 },
      { value: 'moderate', label: '5-10% of revenue', points: 7 },
      { value: 'low', label: '2-5% of revenue', points: 5 },
      { value: 'minimal', label: 'Less than 2% of revenue', points: 3 }
    ]
  },
  {
    id: 'biggest-challenge',
    category: 'Business Challenges',
    question: 'What is your biggest operational pain point?',
    type: 'single',
    weight: 1.5,
    options: [
      { value: 'efficiency', label: 'Process inefficiencies and manual work', points: 10 },
      { value: 'data', label: 'Data analysis and insights', points: 9 },
      { value: 'customer', label: 'Customer service response times', points: 8 },
      { value: 'quality', label: 'Quality control and errors', points: 7 }
    ]
  },
  {
    id: 'competitive-pressure',
    category: 'Market Pressure',
    question: 'Are your competitors using AI?',
    type: 'scale',
    weight: 1.2,
    options: [
      { value: 1, label: "Don't Know", points: 2 },
      { value: 2, label: 'Few Are', points: 4 },
      { value: 3, label: 'Some Are', points: 6 },
      { value: 4, label: 'Many Are', points: 8 },
      { value: 5, label: 'Most Are', points: 10 }
    ]
  },
  {
    id: 'decision-speed',
    category: 'Organizational Agility',
    question: 'How quickly can you implement new technology?',
    type: 'single',
    weight: 1.0,
    options: [
      { value: 'fast', label: 'Within weeks - very agile', points: 10 },
      { value: 'moderate', label: 'Within 1-3 months', points: 7 },
      { value: 'slow', label: '3-6 months', points: 4 },
      { value: 'very-slow', label: 'More than 6 months', points: 2 }
    ]
  },
  {
    id: 'growth-goals',
    category: 'Growth Ambition',
    question: 'What are your 2-year growth targets?',
    type: 'single',
    weight: 1.1,
    options: [
      { value: 'aggressive', label: '50%+ growth', points: 10 },
      { value: 'strong', label: '25-50% growth', points: 8 },
      { value: 'moderate', label: '10-25% growth', points: 6 },
      { value: 'maintain', label: 'Maintain current position', points: 4 }
    ]
  }
];

const AIReadinessAssessment: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<{ [key: string]: any }>({});
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState<AssessmentResults | null>(null);
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    website: '' // Honeypot field - bots will fill this
  });
  const [showContactForm, setShowContactForm] = useState(false);
  const [logoBase64, setLogoBase64] = useState<string>('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Convert white logo to base64 on component mount
  useEffect(() => {
    const convertImageToBase64 = () => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function() {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          const dataURL = canvas.toDataURL('image/png');
          setLogoBase64(dataURL);
        }
      };
      img.src = logoWhite;
    };
    
    convertImageToBase64();
  }, []);

  const progress = ((currentQuestion + (showResults ? 1 : 0)) / (questions.length + 1)) * 100;

  const handleAnswer = (questionId: string, value: any) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    
    // Auto-advance after a short delay for better UX
    setTimeout(() => {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(prev => prev + 1);
      } else {
        calculateResults();
      }
    }, 200); // Quick 200ms delay - fast response to selection
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      calculateResults();
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const calculateResults = () => {
    const categoryScores: { [key: string]: { score: number; maxScore: number; percentage: number; recommendations: string[] } } = {};
    let totalScore = 0;
    let maxScore = 0;

    questions.forEach(question => {
      const answer = answers[question.id];
      if (!answer) return;

      const category = question.category;
      if (!categoryScores[category]) {
        categoryScores[category] = { score: 0, maxScore: 0, percentage: 0, recommendations: [] };
      }

      let questionScore = 0;
      const questionMaxScore = 10 * question.weight;

      if (question.type === 'single' || question.type === 'scale') {
        const option = question.options.find(opt => opt.value === answer);
        if (option) {
          questionScore = option.points * question.weight;
        }
      }

      categoryScores[category].score += questionScore;
      categoryScores[category].maxScore += questionMaxScore;
      totalScore += questionScore;
      maxScore += questionMaxScore;
    });

    // Calculate percentages
    Object.keys(categoryScores).forEach(category => {
      const catScore = categoryScores[category];
      catScore.percentage = (catScore.score / catScore.maxScore) * 100;
    });

    const percentage = (totalScore / maxScore) * 100;
    let grade = 'F';
    let readinessLevel = 'Not Ready';

    if (percentage >= 85) {
      grade = 'A';
      readinessLevel = 'Highly Ready for AI Implementation';
    } else if (percentage >= 70) {
      grade = 'B';
      readinessLevel = 'Ready with Minor Preparations';
    } else if (percentage >= 55) {
      grade = 'C';
      readinessLevel = 'Moderately Ready';
    } else if (percentage >= 40) {
      grade = 'D';
      readinessLevel = 'Significant Preparation Needed';
    }

    const categoryPercentages = Object.values(categoryScores)
      .map(score => score.percentage || 0)
      .filter(value => !Number.isNaN(value));

    let categoryVariance = 0;
    if (categoryPercentages.length > 0) {
      const highest = Math.max(...categoryPercentages);
      const lowest = Math.min(...categoryPercentages);
      categoryVariance = highest - lowest;
    }

    const insights = buildAssessmentInsights({
      answers,
      categoryScores,
      overallPercentage: percentage,
      grade,
      categoryVariance
    });

    const quickWins = insights.quickWins.map(match => match.message);
    const recommendations = insights.recommendations.map(match => match.message);

    // Estimate ROI
    let estimatedROI = '20-35%';
    if (percentage >= 70) estimatedROI = '35-50%';
    else if (percentage >= 40) estimatedROI = '25-40%';

    setResults({
      totalScore,
      maxScore,
      grade,
      categoryScores,
      quickWins,
      recommendations,
      estimatedROI,
      readinessLevel
    });

    setShowResults(true);
  };

  const generatePDF = (returnBase64: boolean = false) => {
    if (!results) return null;

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'a4',
      putOnlyUsedFonts: true,
      compress: true
    });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 40;
    const contentWidth = pageWidth - (margin * 2);
    
    // PAGE 1 - Header with logo
    pdf.setFillColor(11, 152, 206);
    pdf.rect(0, 0, pageWidth, 80, 'F');
    
    // Add white logo image (top right)
    if (logoBase64) {
      try {
        // Logo dimensions: 1024x305 original, scale to fit
        const logoWidth = 150;
        const logoHeight = 45;
        pdf.addImage(logoBase64, 'PNG', pageWidth - margin - logoWidth, 18, logoWidth, logoHeight);
      } catch (e) {
        console.error('Could not add logo to PDF:', e);
        // Fallback to text
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(22);
        pdf.setFont(undefined, 'bold');
        pdf.text('DAVINCI AI SOLUTIONS', pageWidth - margin, 40, { align: 'right' });
      }
    } else {
      // Fallback text logo
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont(undefined, 'bold');
      pdf.text('DAVINCI AI SOLUTIONS', pageWidth - margin, 40, { align: 'right' });
    }
    
    // Report title (left side)
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(16);
    pdf.setFont(undefined, 'bold');
    pdf.text('AI Readiness Assessment Report', margin, 35);
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(11);
    pdf.text('Your Personalized AI Implementation Roadmap', margin, 52);
    pdf.setFontSize(9);
    pdf.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), margin, 67);
    
    // Reset colors
    pdf.setTextColor(0, 0, 0);
    
    // Company info section
    pdf.setFillColor(248, 250, 252);
    pdf.rect(margin, 95, contentWidth, 35, 'F');
    pdf.setDrawColor(11, 152, 206);
    pdf.setLineWidth(0.5);
    pdf.rect(margin, 95, contentWidth, 35, 'S');
    
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'bold');
    pdf.text(`Organization: ${contactInfo.company || 'Not provided'}`, margin + 10, 110);
    pdf.setFont(undefined, 'normal');
    pdf.text(`Contact: ${contactInfo.name || 'Not provided'}`, margin + 10, 122);
    pdf.text(`Date: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, pageWidth - margin - 10, 110, { align: 'right' });
    pdf.text(`Email: ${contactInfo.email || 'Not provided'}`, pageWidth - margin - 10, 122, { align: 'right' });
    
    // Grade Section - Centered and prominent
    const gradeBoxY = 150;
    pdf.setFillColor(250, 252, 255);
    pdf.rect(margin, gradeBoxY, contentWidth, 100, 'F');
    
    pdf.setFontSize(80);
    if (results.grade === 'A') {
      pdf.setTextColor(16, 185, 129);
    } else if (results.grade === 'B') {
      pdf.setTextColor(11, 152, 206);
    } else if (results.grade === 'C') {
      pdf.setTextColor(251, 146, 60);
    } else {
      pdf.setTextColor(239, 68, 68);
    }
    pdf.text(results.grade, pageWidth / 2, gradeBoxY + 45, { align: 'center' });
    
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(16);
    pdf.setFont(undefined, 'bold');
    pdf.text(results.readinessLevel, pageWidth / 2, gradeBoxY + 70, { align: 'center' });
    
    const percentage = (results.totalScore / results.maxScore) * 100;
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(12);
    pdf.text(`Overall Score: ${percentage.toFixed(1)}%`, pageWidth / 2, gradeBoxY + 88, { align: 'center' });
    
    // ROI Highlight Box
    pdf.setFillColor(240, 253, 244);
    pdf.rect(margin, 260, contentWidth, 30, 'F');
    pdf.setDrawColor(16, 185, 129);
    pdf.setLineWidth(1);
    pdf.rect(margin, 260, contentWidth, 30, 'S');
    pdf.setTextColor(16, 185, 129);
    pdf.setFontSize(13);
    pdf.setFont(undefined, 'bold');
    pdf.text(`Estimated Efficiency Gains: ${results.estimatedROI}`, pageWidth / 2, 278, { align: 'center' });
    
    // Category Performance Section
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(14);
    pdf.setFont(undefined, 'bold');
    pdf.text('Performance by Category', margin, 315);
    
    let yPos = 330;
    pdf.setFontSize(9);
    Object.entries(results.categoryScores).forEach(([category, scores]) => {
      if (yPos > pageHeight - 100) return; // Stop if we're near the bottom
      
      // Category name
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(60, 60, 60);
      pdf.text(category, margin, yPos);
      
      // Progress bar
      const barWidth = 180;
      const barHeight = 8;
      const barX = margin + 150;
      
      // Background
      pdf.setFillColor(240, 240, 240);
      pdf.rect(barX, yPos - 6, barWidth, barHeight, 'F');
      
      // Fill
      const fillWidth = (scores.percentage / 100) * barWidth;
      if (scores.percentage >= 70) pdf.setFillColor(16, 185, 129);
      else if (scores.percentage >= 40) pdf.setFillColor(59, 130, 246);
      else pdf.setFillColor(239, 68, 68);
      pdf.rect(barX, yPos - 6, fillWidth, barHeight, 'F');
      
      // Percentage
      pdf.setTextColor(0, 0, 0);
      pdf.setFont(undefined, 'bold');
      pdf.text(`${scores.percentage.toFixed(0)}%`, barX + barWidth + 8, yPos);
      
      yPos += 16;
    });
    
    // PAGE 2 - Action Items
    pdf.addPage();
    
    // Add header for page 2
    pdf.setFillColor(11, 152, 206);
    pdf.rect(0, 0, pageWidth, 40, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(18);
    pdf.setFont(undefined, 'bold');
    pdf.text('Your AI Implementation Action Plan', pageWidth / 2, 25, { align: 'center' });
    
    yPos = 70;

    // Quick Wins Section - Calculate height first
    const quickWinsStartY = yPos;
    let quickWinsHeight = 35; // Header space

    // Calculate content height
    results.quickWins.slice(0, 3).forEach((win, index) => {
      const bulletPoint = `${index + 1}. ${win}`;
      const lines = pdf.splitTextToSize(bulletPoint, contentWidth - 30);
      quickWinsHeight += (lines.length * 13) + 3;
    });
    quickWinsHeight += 10; // Bottom padding

    // Draw box with calculated height
    pdf.setFillColor(240, 253, 244);
    pdf.rect(margin, quickWinsStartY, contentWidth, quickWinsHeight, 'F');
    pdf.setDrawColor(16, 185, 129);
    pdf.setLineWidth(1);
    pdf.rect(margin, quickWinsStartY, contentWidth, quickWinsHeight, 'S');

    pdf.setTextColor(16, 185, 129);
    pdf.setFontSize(14);
    pdf.setFont(undefined, 'bold');
    pdf.text('Quick Wins - Start Here', margin + 10, yPos + 20);

    yPos += 35;
    pdf.setTextColor(0, 0, 0);
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(10);

    results.quickWins.slice(0, 3).forEach((win, index) => {
      const bulletPoint = `${index + 1}. ${win}`;
      const lines = pdf.splitTextToSize(bulletPoint, contentWidth - 30);
      lines.forEach((line: string) => {
        pdf.text(line, margin + 15, yPos);
        yPos += 13;
      });
      yPos += 3;
    });
    
    // Strategic Recommendations - Dynamic spacing
    yPos = quickWinsStartY + quickWinsHeight + 20; // 20px gap between sections
    const strategicStartY = yPos;
    let strategicHeight = 35; // Header space

    // Calculate content height
    results.recommendations.slice(0, 3).forEach((rec) => {
      const bulletRec = `• ${rec}`;
      const lines = pdf.splitTextToSize(bulletRec, contentWidth - 30);
      strategicHeight += (lines.length * 13) + 3;
    });
    strategicHeight += 10; // Bottom padding

    // Draw box with calculated height
    pdf.setFillColor(245, 250, 255);
    pdf.rect(margin, strategicStartY, contentWidth, strategicHeight, 'F');
    pdf.setDrawColor(11, 152, 206);
    pdf.setLineWidth(1);
    pdf.rect(margin, strategicStartY, contentWidth, strategicHeight, 'S');

    pdf.setTextColor(11, 152, 206);
    pdf.setFontSize(14);
    pdf.setFont(undefined, 'bold');
    pdf.text('Strategic Recommendations', margin + 10, yPos + 20);

    yPos += 35;
    pdf.setTextColor(0, 0, 0);
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(10);

    results.recommendations.slice(0, 3).forEach((rec, index) => {
      const bulletRec = `• ${rec}`;
      const lines = pdf.splitTextToSize(bulletRec, contentWidth - 30);
      lines.forEach((line: string) => {
        pdf.text(line, margin + 15, yPos);
        yPos += 13;
      });
      yPos += 3;
    });
    
    // Next Steps - Dynamic spacing
    yPos = strategicStartY + strategicHeight + 20; // 20px gap between sections
    const nextStepsStartY = yPos;
    let nextStepsHeight = 35; // Header space

    const nextSteps = [
      'Schedule a free consultation to discuss your specific needs',
      'Get a customized implementation roadmap for your organization',
      'Identify the highest-ROI AI opportunities for your business',
      'Develop a phased approach to AI adoption'
    ];

    // Calculate content height
    nextSteps.forEach((step, index) => {
      const stepText = `${index + 1}. ${step}`;
      const lines = pdf.splitTextToSize(stepText, contentWidth - 30);
      nextStepsHeight += (lines.length * 13) + 3;
    });
    nextStepsHeight += 10; // Bottom padding

    // Draw box with calculated height
    pdf.setFillColor(255, 251, 245);
    pdf.rect(margin, nextStepsStartY, contentWidth, nextStepsHeight, 'F');
    pdf.setDrawColor(251, 146, 60);
    pdf.setLineWidth(1);
    pdf.rect(margin, nextStepsStartY, contentWidth, nextStepsHeight, 'S');
    
    pdf.setTextColor(251, 146, 60);
    pdf.setFontSize(14);
    pdf.setFont(undefined, 'bold');
    pdf.text('Your Next Steps', margin + 10, yPos + 20);

    yPos += 35;
    pdf.setTextColor(0, 0, 0);
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(10);

    // Render Next Steps content
    nextSteps.forEach((step, index) => {
      const stepText = `${index + 1}. ${step}`;
      const lines = pdf.splitTextToSize(stepText, contentWidth - 30);
      lines.forEach((line: string) => {
        pdf.text(line, margin + 15, yPos);
        yPos += 13;
      });
      yPos += 3;
    });
    
    // Call to Action Box with Logo - Position dynamically
    yPos = nextStepsStartY + nextStepsHeight + 20;

    // Check if we need a new page
    if (yPos + 80 > pageHeight - 40) {
      pdf.addPage();
      yPos = 40;
    }
    pdf.setFillColor(11, 152, 206);
    pdf.rect(margin, yPos, contentWidth, 80, 'F');
    
    // Add white logo in call to action box (bottom left)
    if (logoBase64) {
      try {
        const footerLogoWidth = 100;
        const footerLogoHeight = 30;
        pdf.addImage(logoBase64, 'PNG', margin + 10, yPos + 40, footerLogoWidth, footerLogoHeight);
      } catch (e) {
        // Fallback to text
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(18);
        pdf.setFont(undefined, 'bold');
        pdf.text('DAVINCI AI', margin + 15, yPos + 60);
      }
    } else {
      // Fallback text logo
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);
      pdf.setFont(undefined, 'bold');
      pdf.text('DAVINCI AI', margin + 15, yPos + 60);
    }
    
    pdf.setFontSize(14);
    pdf.setFont(undefined, 'bold');
    pdf.text('Ready to Transform Your Business with AI?', pageWidth / 2, yPos + 25, { align: 'center' });
    
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(11);
    pdf.text('Contact us today for your free consultation', pageWidth / 2, yPos + 42, { align: 'center' });
    
    // Contact info on the right side - properly aligned
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(255, 255, 255);
    pdf.text('info@davincisolutions.ai', pageWidth - margin - 10, yPos + 55, { align: 'right' });
    pdf.text('(403) 245-9429', pageWidth - margin - 10, yPos + 68, { align: 'right' });
    
    // Small footer
    const footerY = pageHeight - 20;
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(128, 128, 128);
    pdf.text(`Generated on ${new Date().toLocaleDateString()} | Confidential`, pageWidth / 2, footerY, { align: 'center' });
    
    // Return base64 or download
    if (returnBase64) {
      // Get base64 string without data URI prefix
      const base64 = pdf.output('datauristring').split(',')[1];
      return base64;
    } else {
      pdf.save(`AI_Readiness_Assessment_${contactInfo.company || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`);
      return null;
    }
  };

  const handleSubmitResults = async () => {
    try {
      // Check honeypot - if filled, it's a bot
      if (contactInfo.website) {
        // Silently fail for bots - they think it worked
        setToast({
          message: 'Success! Your assessment report has been emailed to you.',
          type: 'success'
        });
        setTimeout(() => {
          if (onClose) onClose();
        }, 3000);
        return;
      }
      
      // Ensure results are calculated
      if (!results) {
        console.error('Assessment results not available');
        setToast({
          message: 'Please complete the assessment before submitting.',
          type: 'error'
        });
        return;
      }

      // Execute reCAPTCHA
      if (!executeRecaptcha) {
        console.log('reCAPTCHA not available yet');
        return;
      }

      const recaptchaToken = await executeRecaptcha('assessment_submit');

      // Generate PDF as base64
      const pdfBase64 = generatePDF(true);
      const pdfFilename = `AI_Readiness_Assessment_${contactInfo.company || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`;

      // Log what we're sending for debugging
      console.log('Sending assessment with results:', {
        totalScore: results.totalScore,
        maxScore: results.maxScore,
        grade: results.grade
      });

      const response = await fetch('/api/assessment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...contactInfo,
          answers,
          results,
          timestamp: new Date().toISOString(),
          pdfReport: pdfBase64,
          pdfFilename: pdfFilename,
          recaptchaToken: recaptchaToken
        })
      });

      if (response.ok) {
        setToast({
          message: 'Success! Your assessment report has been emailed to you.',
          type: 'success'
        });
        setTimeout(() => {
          if (onClose) onClose();
        }, 3000);
      } else {
        throw new Error('Failed to submit assessment');
      }
    } catch (error) {
      console.error('Error saving assessment:', error);
      // Fallback to direct download if submission fails
      generatePDF(false);
      setToast({
        message: 'We couldn\'t send your report via email, but it has been downloaded to your device.',
        type: 'error'
      });
    }
  };

  if (showContactForm && results) {
    return (
      <>
        <AssessmentContainer>
          <QuestionContainer>
          <QuestionHeader>
            <CategoryBadge>
              <DocumentArrowDownIcon />
              Final Step
            </CategoryBadge>
            <QuestionText>Get Your Personalized AI Implementation Roadmap</QuestionText>
            <QuestionDescription>
              We'll send you a detailed report with specific recommendations for your organization.
            </QuestionDescription>
          </QuestionHeader>
          
          <ContactForm onSubmit={(e) => { e.preventDefault(); handleSubmitResults(); }}>
            <FormInput
              type="text"
              placeholder="Your Name *"
              required
              value={contactInfo.name}
              onChange={(e) => setContactInfo(prev => ({ ...prev, name: e.target.value }))}
            />
            <FormInput
              type="email"
              placeholder="Work Email *"
              required
              value={contactInfo.email}
              onChange={(e) => setContactInfo(prev => ({ ...prev, email: e.target.value }))}
            />
            <FormInput
              type="text"
              placeholder="Company Name *"
              required
              value={contactInfo.company}
              onChange={(e) => setContactInfo(prev => ({ ...prev, company: e.target.value }))}
            />
            <FormInput
              type="tel"
              placeholder="Phone (Optional)"
              value={contactInfo.phone}
              onChange={(e) => setContactInfo(prev => ({ ...prev, phone: e.target.value }))}
            />
            
            {/* Honeypot field - hidden from real users */}
            <input
              type="text"
              name="website"
              value={contactInfo.website}
              onChange={(e) => setContactInfo(prev => ({ ...prev, website: e.target.value }))}
              style={{ 
                position: 'absolute',
                left: '-9999px',
                width: '1px',
                height: '1px',
                overflow: 'hidden'
              }}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />
            
            <NavigationContainer>
              <NavButton onClick={() => setShowContactForm(false)}>
                <ChevronLeftIcon />
                Back
              </NavButton>
              <NavButton type="submit" primary>
                Get My Roadmap
                <DocumentArrowDownIcon />
              </NavButton>
            </NavigationContainer>
          </ContactForm>
        </QuestionContainer>
      </AssessmentContainer>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.type === 'success' ? 5000 : 7000}
          onClose={() => setToast(null)}
        />
      )}
      </>
    );
  }

  if (showResults && results) {
    const percentage = (results.totalScore / results.maxScore) * 100;
    
    return (
      <>
        <AssessmentContainer>
          <ResultsContainer>
          <GradeSection>
            <GradeLetter grade={results.grade}>{results.grade}</GradeLetter>
            <ReadinessLevel>{results.readinessLevel}</ReadinessLevel>
            <ScoreDisplay>Overall Score: {percentage.toFixed(0)}%</ScoreDisplay>
          </GradeSection>
          
          <MetricsGrid>
            <MetricCard>
              <h4>Readiness Level</h4>
              <p>{results.grade}</p>
            </MetricCard>
            <MetricCard>
              <h4>Efficiency Gains</h4>
              <p>{results.estimatedROI}</p>
            </MetricCard>
            <MetricCard>
              <h4>Implementation Time</h4>
              <p>{percentage >= 70 ? '2-3 months' : '3-6 months'}</p>
            </MetricCard>
          </MetricsGrid>
          
          <QuickWinsCard>
            <h3>
              <RocketLaunchIcon />
              Quick Wins to Start Today
            </h3>
            <ul>
              {results.quickWins.map((win, index) => (
                <li key={index}>{win}</li>
              ))}
            </ul>
          </QuickWinsCard>
          
          <NavigationContainer>
            <NavButton onClick={() => { setShowResults(false); setCurrentQuestion(0); }}>
              Retake Assessment
            </NavButton>
            <NavButton primary onClick={() => setShowContactForm(true)}>
              Get Detailed Roadmap
              <DocumentArrowDownIcon />
            </NavButton>
          </NavigationContainer>
        </ResultsContainer>
      </AssessmentContainer>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.type === 'success' ? 5000 : 7000}
          onClose={() => setToast(null)}
        />
      )}
      </>
    );
  }

  const question = questions[currentQuestion];
  if (!question) {
    // Fallback if question is undefined
    return <div>Loading...</div>;
  }
  
  const currentAnswer = answers[question.id];
  const getCategoryIcon = () => {
    if (question.category.includes('Technology')) return <SparklesIcon />;
    if (question.category.includes('Data')) return <ChartBarIcon />;
    return <RocketLaunchIcon />;
  };

  return (
    <>
      <AssessmentContainer>
      <ProgressSection>
        <ProgressBar>
          <ProgressFill progress={progress} />
        </ProgressBar>
        <ProgressText>
          <span>Question {currentQuestion + 1} of {questions.length}</span>
          <span>{Math.round(progress)}% Complete</span>
        </ProgressText>
      </ProgressSection>
      
      <QuestionContainer key={question.id}>
        <QuestionHeader>
          <CategoryBadge>
            {getCategoryIcon()}
            {question.category}
          </CategoryBadge>
          <QuestionText>{question.question}</QuestionText>
          {question.description && (
            <QuestionDescription>{question.description}</QuestionDescription>
          )}
        </QuestionHeader>
        
        {question.type === 'single' && (
          <OptionsContainer>
            {question.options.map((option, index) => (
              <OptionCard
                key={option.value}
                selected={currentAnswer === option.value}
                onClick={() => handleAnswer(question.id, option.value)}
                tabIndex={index}
              >
                <span>{option.label}</span>
                {currentAnswer === option.value && <CheckMark />}
              </OptionCard>
            ))}
          </OptionsContainer>
        )}
        
        {question.type === 'scale' && (
          <>
            <ScaleContainer>
              {question.options.map(option => (
                <ScaleButton
                  key={option.value}
                  selected={currentAnswer === option.value}
                  onClick={() => handleAnswer(question.id, option.value)}
                >
                  {option.value}
                </ScaleButton>
              ))}
            </ScaleContainer>
            <ScaleLabels>
              <ScaleLabel align="left">{question.options[0].label}</ScaleLabel>
              <ScaleLabel align="right">{question.options[question.options.length - 1].label}</ScaleLabel>
            </ScaleLabels>
          </>
        )}
        
        <NavigationContainer>
          <NavButton 
            onClick={handlePrev} 
            disabled={currentQuestion === 0}
            style={{ 
              visibility: currentQuestion === 0 ? 'hidden' : 'visible' 
            }}
          >
            <ChevronLeftIcon />
            Back
          </NavButton>
          
          <StepIndicator>
            {currentQuestion + 1} / {questions.length}
          </StepIndicator>
          
          <div style={{ minWidth: '120px', textAlign: 'right' }}>
            {currentAnswer && (
              <NavButton 
                onClick={handleNext} 
                primary
              >
                {currentQuestion === questions.length - 1 ? 'See Results' : 'Next'}
                <ChevronRightIcon />
              </NavButton>
            )}
            {!currentAnswer && (
              <span style={{ 
                fontSize: 'var(--fs-sm)', 
                color: 'var(--text-tertiary)',
                fontStyle: 'italic'
              }}>
                Select an answer
              </span>
            )}
          </div>
        </NavigationContainer>
      </QuestionContainer>
      </AssessmentContainer>
      {toast && (
      <Toast
        message={toast.message}
        type={toast.type}
        duration={toast.type === 'success' ? 5000 : 7000}
        onClose={() => setToast(null)}
      />
    )}
    </>
  );
};

export default AIReadinessAssessment;
